import {
    ActionModal,
    FormLayout,
    Label,
    Select,
    TextInput,
} from '@kcftech/design-system';
import { IntegrationCreateRequest, IntegrationType } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { RadioButtonGroup } from 'components/forms/RadioButtonGroup';
import { SelectOption } from 'components/forms/Select';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { AuthFields } from 'pages/Settings/Integrations/AuthFields';
import {
    DefaultIntegrationType,
    IntegrationMaxChar,
} from 'pages/Settings/Integrations/shared';
import {
    AuthenticationType,
    IntegrationDetails,
    SoftwareNameOptions,
} from 'pages/Settings/Integrations/types';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { createIntegration } from 'utils/integration';
import { Filter } from 'utils/query_v3';
import { isUrlValid } from 'utils/validation';

export const authenticationTypeOptions: { value: AuthenticationType; label: string }[] = [
    { label: 'UserID/Password', value: AuthenticationType.Basic },
    { label: 'OAuth', value: AuthenticationType.OAuth },
    { label: 'API Key', value: AuthenticationType.ApiKey },
];

const IntegrationTypeMaximum = {
    [IntegrationType.Cmms]: 2,
    [IntegrationType.IssuePublisher]: 1,
};

const IntegrationTypeOptions: SelectOption[] = Object.keys(IntegrationType)
    .map((value) => ({
        title: value.toUpperCase(),
        value,
    }))
    // Explicitly exclude IntegrationType.IssuePublisher for now since it's not supported in the UI.
    .filter((option) => option.value !== IntegrationType.IssuePublisher);

const softwareNameOptions = (integrationType: IntegrationType) =>
    SoftwareNameOptions[integrationType].map((value: string) => ({
        title: value,
        value,
    }));

export type CreateIntegrationModalProps = Readonly<{
    locationId: string;
    onCreate: () => void;
    onClose: () => void;
}>;

const isSoftwareNameCustom = (softwareName: string): boolean =>
    softwareName.toLowerCase() === 'custom api';

const validateIntegrationType = async (
    locationId: string,
    integrationType: IntegrationType,
    filter: string | undefined,
) => {
    const exceedsMaximumIntegrations =
        (
            await sdClient.getIntegrationsForLocation({
                id: locationId,
                filter,
            })
        ).Total >= IntegrationTypeMaximum[integrationType];
    if (exceedsMaximumIntegrations) {
        return 'Maximum number of CMMS integrations reached';
    }
    return undefined;
};

const validateSoftwareName = async (
    locationId: string,
    name: string,
    filter: string | undefined,
) => {
    const nameFilter = Filter.and(filter, Filter.eq('SoftwareName', name));
    const integrationExists =
        (
            await sdClient.getIntegrationsForLocation({
                id: locationId,
                filter: nameFilter,
            })
        ).Total > 0;

    return integrationExists ? `Integration using ${name} already exists` : undefined;
};

export type IntegrationsFormData = Readonly<{
    IntegrationType: IntegrationType;
    CustomSoftwareName: string;
}> &
    IntegrationDetails;

export function CreateIntegrationModal({
    locationId,
    onCreate,
    onClose,
}: CreateIntegrationModalProps) {
    const { showToast } = useNotificationContext();

    const form = useForm<IntegrationsFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            IntegrationType: DefaultIntegrationType,
            systemid: softwareNameOptions(DefaultIntegrationType)[0].value,
        },
    });

    const {
        watch,
        register,
        setValue,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const {
        IntegrationType: selectedIntegrationType,
        systemid: selectedSoftwareName,
        authtype: selectedAuthType,
    } = watch();

    const filter = useMemo(
        () =>
            Filter.and(
                Filter.eq('IntegrationType', selectedIntegrationType),
                Filter.eq('IsDeleted', false),
            ),
        [selectedIntegrationType],
    );
    const isCustomSoftwareName = isSoftwareNameCustom(selectedSoftwareName);

    const { mutate: createNewIntegration, isPending } = useMutation({
        meta: { name: 'create-integration' },
        mutationFn: async (data: IntegrationsFormData) => {
            const softwareName = isCustomSoftwareName
                ? data.CustomSoftwareName
                : data.systemid;
            const integrationData = new IntegrationCreateRequest({
                IntegrationType: data.IntegrationType,
                SoftwareName: softwareName,
            });
            const configuration: IntegrationDetails = {
                base_url: data.base_url,
                systemid: softwareName,
                id: data.id,
                secret: data.secret,
                authtype: data.authtype,
                tokenurl: data.tokenurl,
            };
            return createIntegration({
                locationId,
                data: integrationData,
                configuration,
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully created integration',
            });
            onCreate();
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to create integration' }),
    });

    const onSave = handleSubmit((formData) => createNewIntegration(formData));

    const onChangeAuthType = (authType: AuthenticationType) => {
        setValue('authtype', authType);
        setValue('tokenurl', undefined);
        setValue('secret', undefined);
        setValue('id', undefined);
    };

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={onClose}
            title="Create an Integration"
            actionButtonText="Create"
            closeButtonText="Cancel"
            height={750}
        >
            <SpinnerArea isLoading={isPending || isSubmitting}>
                <FormLayout>
                    <CapitalizedValuesSelect
                        ariaLabel="select integration type"
                        required
                        label="Integration Type"
                        error={errors.IntegrationType?.message}
                        value={selectedIntegrationType}
                        options={IntegrationTypeOptions}
                        {...register('IntegrationType', {
                            required: 'Integration Type is required',
                            validate: (value) =>
                                validateIntegrationType(locationId, value, filter),
                        })}
                    />
                    <Select
                        ariaLabel="select software name"
                        required
                        label="Software Name"
                        error={errors.systemid?.message}
                        options={softwareNameOptions(selectedIntegrationType)}
                        {...register('systemid', {
                            required: 'Software Name is required',
                            validate: (value) =>
                                validateSoftwareName(locationId, value, filter),
                            onChange: () => setValue('CustomSoftwareName', ''),
                        })}
                    />
                    {isCustomSoftwareName && (
                        <TextInput
                            label={`Custom ${selectedIntegrationType.toUpperCase()} Name`}
                            required
                            error={errors.CustomSoftwareName?.message}
                            placeholder={`Enter a custom ${selectedIntegrationType.toUpperCase()} name`}
                            {...register('CustomSoftwareName', {
                                maxLength: {
                                    value: IntegrationMaxChar,
                                    message: `Name must be less than ${IntegrationMaxChar} characters`,
                                },
                                validate: (value) =>
                                    validateSoftwareName(locationId, value, filter),
                            })}
                        />
                    )}
                    <TextInput
                        label="Integration URL"
                        error={errors.base_url?.message?.toString()}
                        placeholder="e.g., https://servername.com"
                        {...register('base_url', {
                            maxLength: {
                                value: IntegrationMaxChar,
                                message: `URL must be less than ${IntegrationMaxChar} characters`,
                            },
                            validate: (value) =>
                                value && !isUrlValid(value) ? 'Invalid URL' : undefined,
                        })}
                    />
                    <Label label="Authentication Type">
                        <RadioButtonGroup
                            className="mt-5"
                            name="authType"
                            layout="horizontal"
                            radioStyle="indicator"
                            items={authenticationTypeOptions}
                            value={selectedAuthType}
                            onChange={onChangeAuthType}
                        />
                    </Label>
                    {selectedAuthType && (
                        <AuthFields form={form} type={selectedAuthType} />
                    )}
                </FormLayout>
            </SpinnerArea>
        </ActionModal>
    );
}

const CapitalizedValuesSelect = styled(Select)`
    text-transform: capitalize;
`;
